import React, { useEffect, useState } from "react";
import {
    Badge,
    Button,
    Card,
    CardBody,
    Col,
    Container,
    FormGroup,
    Input,
    Row,
    Spinner
} from "reactstrap";
import SeoNoIndex from "../../components/helpers/SeoNoIndex";
import { sessionEnded } from "./SessionEnded";
import {
    createBlankTemplate,
    deleteTemplate,
    duplicateTemplate,
    fetchMyTemplates,
    updateTemplate
} from "../../components/helpers/UserTemplateHelpers";

const trackEvent = (eventName, params = {}) => {
    if (typeof window.gtag === "function") {
        window.gtag("event", eventName, params);
    }
};

export default function MyTemplates() {
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [savingId, setSavingId] = useState(null);
    const [error, setError] = useState(null);
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [newTemplate, setNewTemplate] = useState({ name: "", description: "", visibility: "PRIVATE", items: [] });
    const [createError, setCreateError] = useState(null);
    const [creating, setCreating] = useState(false);

    const loadTemplates = async () => {
        setLoading(true);
        setTemplates(await fetchMyTemplates());
        setLoading(false);
    };

    useEffect(() => {
        loadTemplates();
    }, []);

    if (!sessionStorage.getItem("customer")) {
        return sessionEnded();
    }

    const saveMetadata = async (template, fields) => {
        setSavingId(template.id);
        setError(null);
        const updated = await updateTemplate(template.id, fields);
        setSavingId(null);
        if (!updated) {
            setError("Unable to update this template.");
            return;
        }
        trackEvent("service_plan_template_updated", { template_id: template.id });
        setTemplates(current => current.map(item => item.id === updated.id ? updated : item));
    };

    const handleDuplicate = async (template) => {
        setSavingId(template.id);
        const duplicated = await duplicateTemplate(template.id);
        setSavingId(null);
        if (duplicated) {
            trackEvent("service_plan_template_duplicated", { template_id: template.id, new_template_id: duplicated.id });
            loadTemplates();
        }
    };

    const handleDelete = async (template) => {
        if (!window.confirm("Delete this template?")) {
            return;
        }
        setSavingId(template.id);
        const deleted = await deleteTemplate(template.id);
        setSavingId(null);
        if (deleted) {
            trackEvent("service_plan_template_deleted", { template_id: template.id });
            loadTemplates();
        }
    };

    const accountTemplates = templates.filter(template => template.visibility === "ACCOUNT");
    const privateTemplates = templates.filter(template => template.visibility !== "ACCOUNT");

    const resetNewTemplate = () => {
        setNewTemplate({ name: "", description: "", visibility: "PRIVATE", items: [] });
        setCreateError(null);
    };

    const updateNewSection = (index, fields) => {
        setNewTemplate(current => ({
            ...current,
            items: current.items.map((item, itemIndex) => itemIndex === index ? { ...item, ...fields } : item)
        }));
    };

    const removeNewSection = (index) => {
        setNewTemplate(current => ({
            ...current,
            items: current.items.filter((item, itemIndex) => itemIndex !== index)
        }));
    };

    const saveNewTemplate = async () => {
        if (!newTemplate.name || !newTemplate.name.trim()) {
            setCreateError("Template name is required");
            return;
        }
        const items = (newTemplate.items || [])
            .filter(item => item.title && item.title.trim())
            .map((item, index) => ({
                displayOrder: index,
                title: item.title,
                notes: item.notes,
                itemType: "SERVICE_PART"
            }));
        setCreating(true);
        setCreateError(null);
        const result = await createBlankTemplate({ ...newTemplate, items });
        setCreating(false);
        if (!result) {
            setCreateError("Unable to create this template.");
            return;
        }
        trackEvent("service_plan_template_created", {
            template_id: result.id,
            item_count: items.length
        });
        setShowCreateForm(false);
        resetNewTemplate();
        loadTemplates();
    };

    const renderTemplate = (template) => (
        <Col lg="6" className="mb-4" key={template.id}>
            <Card className="shadow border-0">
                <CardBody>
                    <div className="d-flex justify-content-between align-items-start">
                        <div style={{flex: 1}}>
                            <Input
                                className="font-weight-bold mb-2"
                                defaultValue={template.name}
                                onBlur={(e) => {
                                    if (e.target.value !== template.name) {
                                        saveMetadata(template, { name: e.target.value });
                                    }
                                }}
                            />
                            <Input
                                className="mb-3"
                                type="textarea"
                                defaultValue={template.description || ""}
                                onBlur={(e) => {
                                    if (e.target.value !== (template.description || "")) {
                                        saveMetadata(template, { description: e.target.value });
                                    }
                                }}
                            />
                        </div>
                        <Badge color={template.visibility === "ACCOUNT" ? "primary" : "secondary"} className="ml-3">
                            {template.visibility === "ACCOUNT" ? "Account" : "Private"}
                        </Badge>
                    </div>
                    <p className="description mb-3">{(template.items || []).length} items</p>
                    <FormGroup>
                        <Input
                            type="select"
                            value={template.visibility || "PRIVATE"}
                            onChange={(e) => saveMetadata(template, { visibility: e.target.value })}
                        >
                            <option value="ACCOUNT">Account</option>
                            <option value="PRIVATE">Private</option>
                        </Input>
                    </FormGroup>
                    <div className="d-flex justify-content-end">
                        <Button
                            color="secondary"
                            size="sm"
                            type="button"
                            disabled={savingId === template.id}
                            onClick={() => handleDuplicate(template)}
                        >
                            Duplicate
                        </Button>
                        <Button
                            color="warning"
                            size="sm"
                            type="button"
                            disabled={savingId === template.id}
                            onClick={() => handleDelete(template)}
                        >
                            Delete
                        </Button>
                    </div>
                </CardBody>
            </Card>
        </Col>
    );

    return (
        <>
            <SeoNoIndex />
            <section className="section section-lg">
                <Container>
                    <Row className="mb-4">
                        <Col>
                            <h1 className="display-4">My Templates</h1>
                        </Col>
                    </Row>
                    <Row className="mb-4">
                        <Col>
                            {!showCreateForm ? (
                                <Button color="primary" type="button" onClick={() => setShowCreateForm(true)}>
                                    Create new template
                                </Button>
                            ) : (
                                <Card className="shadow border-0">
                                    <CardBody>
                                        <h2 style={{fontSize: "1rem"}} className="text-primary text-uppercase">Create new template</h2>
                                        <FormGroup>
                                            <Input
                                                placeholder="Template name"
                                                value={newTemplate.name}
                                                onChange={(e) => setNewTemplate({ ...newTemplate, name: e.target.value })}
                                            />
                                        </FormGroup>
                                        <FormGroup>
                                            <Input
                                                type="textarea"
                                                placeholder="Description"
                                                value={newTemplate.description}
                                                onChange={(e) => setNewTemplate({ ...newTemplate, description: e.target.value })}
                                            />
                                        </FormGroup>
                                        <FormGroup>
                                            <Input
                                                type="select"
                                                value={newTemplate.visibility}
                                                onChange={(e) => setNewTemplate({ ...newTemplate, visibility: e.target.value })}
                                            >
                                                <option value="ACCOUNT">Account</option>
                                                <option value="PRIVATE">Private</option>
                                            </Input>
                                        </FormGroup>
                                        {(newTemplate.items || []).map((item, index) => (
                                            <Row key={index} className="mb-2">
                                                <Col md="5">
                                                    <Input
                                                        placeholder="Section title"
                                                        value={item.title || ""}
                                                        onChange={(e) => updateNewSection(index, { title: e.target.value })}
                                                    />
                                                </Col>
                                                <Col md="5">
                                                    <Input
                                                        placeholder="Notes"
                                                        value={item.notes || ""}
                                                        onChange={(e) => updateNewSection(index, { notes: e.target.value })}
                                                    />
                                                </Col>
                                                <Col md="2">
                                                    <Button color="secondary" type="button" onClick={() => removeNewSection(index)}>
                                                        Remove
                                                    </Button>
                                                </Col>
                                            </Row>
                                        ))}
                                        <Button
                                            color="secondary"
                                            type="button"
                                            onClick={() => setNewTemplate(current => ({
                                                ...current,
                                                items: [...current.items, { title: "", notes: "" }]
                                            }))}
                                        >
                                            Add section
                                        </Button>
                                        {createError && <p className="text-danger mt-3">{createError}</p>}
                                        <div className="mt-3">
                                            <Button color="primary" type="button" disabled={creating} onClick={saveNewTemplate}>
                                                {creating ? "Saving..." : "Save template"}
                                            </Button>
                                            <Button
                                                color="secondary"
                                                type="button"
                                                disabled={creating}
                                                onClick={() => {
                                                    setShowCreateForm(false);
                                                    resetNewTemplate();
                                                }}
                                            >
                                                Cancel
                                            </Button>
                                        </div>
                                    </CardBody>
                                </Card>
                            )}
                        </Col>
                    </Row>
                    {error && <p className="text-danger">{error}</p>}
                    {loading ? (
                        <Spinner />
                    ) : (
                        <>
                            {accountTemplates.length > 0 && (
                                <>
                                    <h2 style={{fontSize: "1rem"}} className="text-primary text-uppercase">Account Templates</h2>
                                    <Row>{accountTemplates.map(renderTemplate)}</Row>
                                </>
                            )}
                            <h2 style={{fontSize: "1rem"}} className="text-primary text-uppercase">My Templates</h2>
                            <Row>
                                {privateTemplates.length === 0 ? (
                                    <Col><p className="description">No private templates yet.</p></Col>
                                ) : privateTemplates.map(renderTemplate)}
                            </Row>
                        </>
                    )}
                </Container>
            </section>
        </>
    );
}
